import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useCookies } from "react-cookie";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { HiBars3, HiOutlineBuildingLibrary, HiXMark } from "react-icons/hi2";

import classes from "./Header.module.css";
import { getOneUser } from "../../api/api";
import { bankName } from "../../util/config";

import Nav from "./Nav";

const MOBILE_QUERY = "(max-width: 48em)";

const Header = () => {
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const [photo, setPhoto] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const { jwt } = useCookies(["jwt"])[0];
  const innerRef = useRef(null);
  const userId = useSelector((state) => state.auth.user?._id);
  const location = useLocation();

  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const toggleMenu = useCallback(() => setMenuOpen((open) => !open), []);

  useEffect(() => {
    const request = async () => {
      const res = await getOneUser(jwt, userId);
      if (res.status === "success") {
        setPhoto(res.data.user.photo);
        setIsLoading(false);
      }
    };
    if (userId) {
      request();
    }
  }, [jwt, userId]);

  useEffect(() => {
    closeMenu();
  }, [location.pathname, closeMenu]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 48.0625em)");
    const onWiden = () => {
      if (mq.matches) {
        closeMenu();
      }
    };
    mq.addEventListener("change", onWiden);
    return () => mq.removeEventListener("change", onWiden);
  }, [closeMenu]);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!menuOpen) return;
      if (!window.matchMedia(MOBILE_QUERY).matches) return;
      if (innerRef.current && !innerRef.current.contains(e.target)) {
        closeMenu();
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [menuOpen, closeMenu]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        closeMenu();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [closeMenu]);

  useEffect(() => {
    if (
      menuOpen &&
      typeof window !== "undefined" &&
      window.matchMedia(MOBILE_QUERY).matches
    ) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [menuOpen]);

  const navClassName = [
    classes.navContainer,
    menuOpen ? classes.navOpen : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <header className={classes.header}>
      {menuOpen && (
        <button
          type="button"
          className={classes.backdrop}
          aria-label="Close menu"
          onClick={closeMenu}
        />
      )}
      <div className={classes.inner} ref={innerRef}>
        <Link to="/" className={classes.brand} onClick={closeMenu}>
          <HiOutlineBuildingLibrary
            className={classes.brandIcon}
            aria-hidden
          />
          {bankName}
        </Link>
        <button
          type="button"
          className={classes.menuBtn}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          aria-controls="primary-navigation"
          onClick={toggleMenu}
        >
          {menuOpen ? (
            <HiXMark className={classes.menuIcon} aria-hidden />
          ) : (
            <HiBars3 className={classes.menuIcon} aria-hidden />
          )}
        </button>
        <div className={navClassName}>
          <Nav onNavigate={closeMenu} />
        </div>
        {isLoggedIn && (
          <div className={classes.userSlot}>
            {isLoading && (
              <Skeleton variant="circle" className={classes.skeleton} />
            )}
            {!isLoading && (
              <div className={classes.userImg}>
                <img src={photo} alt="" />
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
